import Popover from 'core/components/dialogs/Popover'
import Input, { InputProps } from 'core/components/inputs/Input'
import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import moment, { Moment } from 'moment'
import cn from 'classnames'
import Button from 'core/components/buttons/Button'
import Calendar from 'core/components/inputs/date-picker/Calendar'
import MonthSwitcher from 'core/components/inputs/date-picker/MonthSwitcher'
import DateInput from 'core/components/inputs/date-picker/DateInput'
import Icon from 'core/components/icons/Icon'

interface DatePickerProps
  extends Omit<
    InputProps,
    | 'onChange'
    | 'className'
    | 'value'
    | 'children'
    | 'suffix'
    | 'prefix'
    | 'type'
  > {
  value?: null | undefined | Moment
  onChange?: (value: Moment | null) => void
  dismissOnSelect?: boolean
  className?: string
  disableAfter?: Moment | null
  disableBefore?: Moment | null
  type?: 'date-picker'
  allowClear?: boolean
  disableToday?: boolean
  hideIcon?: boolean
}

const DatePicker = forwardRef(
  (
    {
      onChange,
      value,
      dismissOnSelect = true,
      className,
      disableBefore,
      disableAfter,
      allowClear = true,
      disableToday = false,
      readOnly,
      hideIcon,
      ...props
    }: DatePickerProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const [menuExpanded, setMenuExpanded] = useState(false)
    const elementRef = useRef<HTMLDivElement>(null)
    const [calendarDate, setCalendarDate] = useState<Moment>(moment())

    const onFocus = useCallback(() => {
      setMenuExpanded(!menuExpanded)
    }, [menuExpanded])

    const onBlur = useCallback(() => {
      setMenuExpanded(false)
    }, [])

    const content = useMemo(() => {
      return (
        <div
          ref={elementRef}
          className={cn(['card-shadow bg-white rounded-md w-[18rem]'])}
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
        >
          <div className="h-[350px] pt-2">
            <div className="flex items-end justify-center gap-2 w-full">
              <DateInput
                label="Selected date"
                date={value ? moment(value) : value}
                onChange={(_date) => {
                  setCalendarDate(_date)
                  onChange?.(_date)
                }}
              />

              <Button
                variant="outline"
                className="h-9 mb-4 flex items-center"
                onClick={() => {
                  setCalendarDate(moment())
                  onChange?.(moment())
                  if (dismissOnSelect) setMenuExpanded(false)
                }}
                disabled={disableToday}
              >
                Today
              </Button>
            </div>
            <div className="border-y border-dark-1 py-2 mb-2">
              <MonthSwitcher
                date={calendarDate}
                onChange={(_date) => {
                  setCalendarDate(_date)
                }}
              />
            </div>

            <Calendar
              value={value}
              onChange={(_date) => {
                if (dismissOnSelect) setMenuExpanded(false)
                if (!calendarDate.isSame(_date, 'month')) setCalendarDate(_date)

                onChange?.(_date)
              }}
              date={calendarDate}
              disableBefore={disableBefore}
              disableAfter={disableAfter}
            />
          </div>
        </div>
      )
    }, [
      value,
      disableToday,
      calendarDate,
      disableBefore,
      disableAfter,
      onChange,
      dismissOnSelect
    ])

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          elementRef.current &&
          !elementRef.current.contains(event.target as Node) &&
          // @ts-ignore
          event?.target?.nodeName !== 'INPUT'
        ) {
          setMenuExpanded(false)
        }
      }

      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          onBlur()
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleEscapeKey)
      }
    }, [onBlur])

    useEffect(() => {
      if (!menuExpanded) {
        if (value) {
          return setCalendarDate(value as Moment)
        }

        setCalendarDate(moment())
      }
    }, [menuExpanded, value])

    const inputValue = useMemo(() => {
      if (value === null || value === undefined) return ''

      return value.format('MMM D, yyyy')
    }, [value])

    return (
      <div onClick={(e) => e.stopPropagation()} className="flex" ref={ref}>
        <Popover
          show={menuExpanded}
          position="bottom-left"
          useArrow={false}
          content={content}
        >
          <Input
            onClick={onFocus}
            value={inputValue}
            placeholder="Select date"
            className={cn(['caret-transparent w-full', className])}
            onChange={console.log}
            autoComplete="off"
            readOnly={readOnly}
            {...props}
            suffix={
              allowClear && !readOnly ? (
                <Icon
                  name="close"
                  noWrapper
                  height={11}
                  width={11}
                  className={cn([
                    'opacity-0 hover:opacity-80 rounded-full bg-dark-3 hover:bg-dark-4',
                    value && 'opacity-100 cursor-pointer'
                  ])}
                  color="text-white"
                  onClick={() => {
                    setMenuExpanded(false)
                    setCalendarDate(moment())
                    onChange?.(null)
                  }}
                />
              ) : undefined
            }
            prefix={
              hideIcon ? undefined : (
                <Icon
                  name="calendar-outline"
                  noWrapper
                  height={16}
                  width={16}
                  className="mb-1"
                />
              )
            }
          />
        </Popover>
      </div>
    )
  }
)

export default DatePicker
